// StorageNext.ts
import { compressToUTF16, decompressFromUTF16 } from 'lz-string'
import type { SavedViz } from './SavedViz.js' // (re-exported from verification.ts)
import { SnapshotEnvelopeSchema, SavedVizSchema } from './Verification.js'

const SCHEMA_VERSION = 'viz-v2'
const MAX_AGE_DAYS = 30

type Clock = () => number
const now: Clock = () => Date.now()

/* key computation for storage */
const kAutosave = (projectId: string) => `ws:${projectId}:autosave:${SCHEMA_VERSION}`
const kAutosaveMeta = (projectId: string) => `ws:${projectId}:meta:${SCHEMA_VERSION}`
const kPrevious = (scopeId: string) => `ws:${scopeId}:previous:${SCHEMA_VERSION}`
const kTemplatesIdx = (projectId: string) => `ws:${projectId}:templates:index:${SCHEMA_VERSION}`
const kTemplate = (projectId: string, id: string) => `ws:${projectId}:templates:${id}:${SCHEMA_VERSION}`
const kIndex = `ws:index:${SCHEMA_VERSION}` // LRU of projects

// ---- Small helpers ------------------------------------------
function safeParse<T>(s: string | null): T | null {
  if (!s) return null
  try {
    return JSON.parse(s) as T
  } catch {
    return null
  }
}

function compress(obj: unknown): string {
  return compressToUTF16(JSON.stringify(obj))
}

function decompressEnvelope(s: string) {
  try {
    const parsed = JSON.parse(decompressFromUTF16(s) || 'null')
    // Validate the ENVELOPE that wraps SavedViz
    const safeParseResult = SnapshotEnvelopeSchema.safeParse(parsed)
    if (!safeParseResult.success) {
      return null
    }
    return safeParseResult.data
  } catch {
    return null
  }
}

const days = (n: number) => n * 24 * 60 * 60 * 1000

// ---- Public API ----------------------------------------------
export type SnapshotEnvelope<T = SavedViz> = {
  schemaVersion: string
  savedAt: number
  appVersion?: string
  payload: T
}

export function saveAutosave(scopeId: string, payload: SavedViz, appVersion?: string) {
  const env: SnapshotEnvelope = { schemaVersion: SCHEMA_VERSION, savedAt: now(), appVersion, payload }
  const body = compress(env)
  localStorage.setItem(kAutosave(scopeId), body)
  localStorage.setItem(kAutosaveMeta(scopeId), JSON.stringify({ lastSavedAt: env.savedAt, size: body.length }))

  // update LRU index of projects (optional cleanup hook)
  const idxRaw = localStorage.getItem(kIndex)
  const idx = safeParse<{ projects: { id: string; last: number }[] }>(idxRaw) ?? { projects: [] }
  const filtered = idx.projects.filter((p) => p.id !== scopeId)
  idx.projects = [{ id: scopeId, last: env.savedAt }, ...filtered].slice(0, 25)
  localStorage.setItem(kIndex, JSON.stringify(idx))
}

export function loadAutosave(projectId: string, { maxAgeDays = MAX_AGE_DAYS }: { maxAgeDays?: number } = {}): SavedViz | null {
  return loadEnvelope(localStorage.getItem(kAutosave(projectId)), maxAgeDays)?.payload ?? null
}

function loadEnvelope(raw: string | null, maxAgeDays: number): SnapshotEnvelope | null {
  if (!raw) return null
  const env = decompressEnvelope(raw)
  if (!env) return null
  if (env.schemaVersion !== SCHEMA_VERSION) return null
  if (now() - env.savedAt > days(maxAgeDays)) return null
  // Defensive: validate payload too (envelope validated already)
  const payloadParse = SavedVizSchema.safeParse(env.payload)
  return payloadParse.success ? { ...env, payload: payloadParse.data as SavedViz } : null
}

/**
 * The previous slot holds the last session the user worked in, so it can be offered for restoring while the
 * current slot follows the open workspace. Copying on open is idempotent as long as an untouched workspace
 * never writes the current slot.
 */
export function rotateAutosaveToPrevious(scopeId: string) {
  const current = localStorage.getItem(kAutosave(scopeId))
  if (current) localStorage.setItem(kPrevious(scopeId), current)
}

export function loadPreviousAutosave(scopeId: string): { saved: SavedViz; savedAt: number } | null {
  const env = loadEnvelope(localStorage.getItem(kPrevious(scopeId)), MAX_AGE_DAYS)
  return env ? { saved: env.payload, savedAt: env.savedAt } : null
}

export function clearPreviousAutosave(scopeId: string) {
  localStorage.removeItem(kPrevious(scopeId))
}

export function clearAutosave(projectId: string) {
  localStorage.removeItem(kAutosave(projectId))
  localStorage.removeItem(kAutosaveMeta(projectId))
}

// ---- Templates -----------------------------------------------
export type TemplateMeta = { id: string; name: string; createdAt: number }

export function listTemplates(projectId: string): TemplateMeta[] {
  return safeParse<TemplateMeta[]>(localStorage.getItem(kTemplatesIdx(projectId))) ?? []
}

export function saveTemplate(projectId: string, meta: TemplateMeta, payload: SavedViz) {
  const env: SnapshotEnvelope = { schemaVersion: SCHEMA_VERSION, savedAt: now(), payload }
  const body = compress(env)
  localStorage.setItem(kTemplate(projectId, meta.id), body)
  const idx = listTemplates(projectId).filter((t) => t.id !== meta.id)
  localStorage.setItem(kTemplatesIdx(projectId), JSON.stringify([{ ...meta }, ...idx]))
}

export function loadTemplate(projectId: string, id: string): SavedViz | null {
  const raw = localStorage.getItem(kTemplate(projectId, id))
  if (!raw) return null
  const env = decompressEnvelope(raw)
  if (!env) return null
  const payloadParse = SavedVizSchema.safeParse(env.payload)
  return payloadParse.success ? (payloadParse.data as SavedViz) : null
}

export function deleteTemplate(projectId: string, id: string) {
  localStorage.removeItem(kTemplate(projectId, id))
  const idx = listTemplates(projectId).filter((t) => t.id !== id)
  localStorage.setItem(kTemplatesIdx(projectId), JSON.stringify(idx))
}
